import { SchemaSyncHandler, open } from '@/db'
import axios from 'axios'
import MobileDetect from 'mobile-detect'
import { getLocationFromDevice } from '@/util'
import globals from '@/globals'
import Draft from '@/classes/Draft'

// make sure db is ready
SchemaSyncHandler.sync()

let db

export default {
  async initDB ({ commit }) {
    if (db) return
    db = await open(process.env.VUE_APP_DB_NAME, process.env.VUE_APP_DB_VERSION)
    commit('DB_OPENED')
    return true
  },

  async logout ({ commit }) {
    commit('LOGOUT')
    try {
      commit('SET_LOADING', true)

      // Get API nonce
      const nonceResponse = await fetch(process.env.VUE_APP_API_DOMAIN, {
        method: 'get',
        credentials: 'include'
      })
      const nonce = nonceResponse.headers.get('X-WP-Nonce')

      // Log out via API
      const logoutUrl = `${process.env.VUE_APP_API_AC_ROOT}logout`
      const logoutRes = await fetch(logoutUrl, {
        method: 'post',
        headers: {
          'X-WP-Nonce': nonce
        },
        credentials: 'include'
      })

      if (logoutRes.status > 204 && logoutRes.status !== 403) {
        throw new Error('Could not sign out of Wordpress')
      }

      commit('SET_LOADING', false)

    } catch (err) {

      commit('SET_LOADING', false)

      throw err
    }
  },

  async startLoginFlow ({ dispatch }) {
    try {
      await dispatch('logout')

      window.location =  process.env.VUE_APP_API_DOMAIN + '?discourse_sso=1&redirect_to=https://content.anthropocene-curriculum.org/login-redirect'
    } catch (err) {
      throw err
    }
  },

  async login ({ dispatch, commit }, { username, password }) {
    try {
      commit('SET_LOADING', true)

      // Request CSRF token
      const csrfUrl = `${process.env.VUE_APP_DISCOURSE_URL}session/csrf?_=${new Date().getTime()}`
      const csrfRes = await fetch(csrfUrl, {
        method: 'get',
        headers: {
          'x-requested-with': 'XMLHttpRequest'
        },
        credentials: 'include'
      })

      const csrfJson = await csrfRes.json()
      const csrfToken = csrfJson.csrf

      const formData = new FormData()
      formData.append('login', username)
      formData.append('password', password)
      formData.append('second_factor_method', 1)

      // Request session with CSRF token and credenetials
      const sessionUrl =  `${process.env.VUE_APP_DISCOURSE_URL}session`

      const sessionRes = await fetch(sessionUrl, {
        method: 'post',
        headers: {
          'x-requested-with': 'XMLHttpRequest',
          'x-csrf-token': csrfToken
        },
        credentials: 'include',
        body: formData
      })

      if (sessionRes.status > 204) {
        throw new Error('Could not sign in to Discourse')
      }

      const sessionJson = await sessionRes.json()

      if (sessionJson.error !== undefined) {
        commit('SET_LOADING', false)
        return sessionJson
      }

      // Login to WP via SSO redirect
      const ssoUrl =  `${process.env.VUE_APP_API_DOMAIN}?discourse_sso=1&amp;redirect_to=${window.location}`
      await fetch(ssoUrl, {
        method: 'get',
        mode: 'no-cors',
        redirect: 'follow',
        credentials: 'include'
      })

      dispatch('postLoginTasks')

      return true
    } catch (error) {
      commit('SET_LOADING', false)
      return error
    }
  },

  async postLoginTasks({ commit, dispatch }) {
    try {
      commit('SET_LOADING', true)

      // Get API nonce
      const nonceResponse = await fetch(process.env.VUE_APP_API_DOMAIN, {
        method: 'get',
        credentials: 'include'
      })

      const nonce = nonceResponse.headers.get('X-WP-Nonce')

      // Get user info
      const userInfoUrl = `${process.env.VUE_APP_API_ROOT}users/me?skip_cache=1&context=edit`

      const userInfoRes = await fetch(userInfoUrl, {
        method: 'get',
        headers: {
          'X-WP-Nonce': nonce
        },
        credentials: 'include'
      })

      if (userInfoRes.status > 204) {
        throw new Error('Could not sign in to Wordpress')
      }

      const userInfo = await userInfoRes.json()

      commit('SET_USER_INFO', userInfo)

      // Delete existing app password
      const path = `wp-json/wp/v2/users/${userInfo.id}/application-passwords`
      const appPasswordUrl = `${process.env.VUE_APP_API_DOMAIN}${path}?name=field_notes&skip_cache=1`

      await fetch(appPasswordUrl, {
        method: 'delete',
        headers: {
          'X-WP-Nonce': nonce
        },
        credentials: 'include'
      })

      // Get app password
      const appPasswordRes = await fetch(appPasswordUrl, {
        method: 'post',
        headers: {
          'X-WP-Nonce': nonce
        },
        credentials: 'include'
      })

      const appPasswordJson = await appPasswordRes.json()
      const appPassword = appPasswordJson.password

      const appCredentials = btoa(`${userInfo.username}:${appPassword}`)

      commit('SET_APP_CREDENTIALS', appCredentials)

      dispatch('setBanner', {
        message: `Logged in as ${userInfo.name}`,
        duration: globals.banner.duration
      })

      commit('SET_LOADING', false)

      // start uploading, if necessary
      dispatch('processQueue')

      // get user notes
      dispatch('getUserNotes')

      return true
    } catch (err) {

      dispatch('setBanner', {
        message: 'Error logging in',
        duration: globals.banner.duration
      })

      commit('SET_LOADING', false)

      throw err
    }
  },

  async processQueue ({ state, dispatch, commit }) {
    if (state.processingQueue) return
    if (!state.appCredentials) {
      dispatch('startLoginFlow')
      return
    }
    commit('QUEUE_STATUS', true)
    while (state.uploadQueue.length) {
      const id = state.uploadQueue[0]
      const draft = state.drafts.find((d) => d.draftKey === id)
      if (draft) {
        await draft.upload(state.appCredentials)
      }
      commit('SHIFT_QUEUE')
    }
    commit('QUEUE_STATUS', false)
  },

  addTag ({ state, dispatch }, { name, type = 'folksonomy' }) {
    if (!state.appCredentials) {
      dispatch('startLoginFlow')
      return
    }
    const data = new FormData()
    data.append('name', name)

    return axios(process.env.VUE_APP_API_ROOT + type, {
      data,
      method: 'post',
      headers: {
        'Authorization': `Basic ${state.appCredentials}`
      }
    }).then(({ data }) => data)
  },

  async storeDraft ({ dispatch, commit }, { key, data }) {
    try {
      if (!db) await dispatch('initDB')

      await db.put(process.env.VUE_APP_STORE_NAME, data, key)

      commit('ADD_DRAFT', data)
    } catch (err) {
      throw new Error(err)
    }
  },

  async getDrafts ({ state, commit, dispatch }) {
    if (state.drafts.length) return
    try {
      if (!db) await dispatch('initDB')
      let cursor = await db.transaction(process.env.VUE_APP_STORE_NAME).store.openCursor()

      while (cursor) {
        let d = new Draft(cursor.value)
        d.createPreviewUrl()
        commit('ADD_DRAFT', d)
        cursor = await cursor.continue()
      }
    } catch (err) {
      throw new Error(err)
    }
  },

  async deleteDraft ({ dispatch, commit }, key) {
    if (!db) await dispatch('initDB')
    await db.delete(process.env.VUE_APP_STORE_NAME, key)
    commit('DELETE_DRAFT', key)
  },

  async truncateAllNotes ({ commit }, count) {
    commit('TRUNCATE_ALL_NOTES', count)
  },

  async getLatestNotes ({ commit }) {
    try {
      const endpoint = 'field_note'
      const query = `?_embed&per_page=${globals.pagination.perPage}`
      const response = await axios.get(`${process.env.VUE_APP_API_ROOT}${endpoint}${query}`)
      commit('UPDATE_LATEST_NOTES', response.data)
      commit('UPDATE_TOTAL_PAGES', response.headers['x-wp-totalpages'])
      return true
    } catch (error) {
      return error
    }
  },

  async getLatestNotesForUser ({ commit }, payload) {
    try {
      const endpoint = 'field_note'
      const query = `?_embed&author=${payload}&per_page=${globals.pagination.perPage}`
      const response = await axios.get(`${process.env.VUE_APP_API_ROOT}${endpoint}${query}`)
      commit('UPDATE_LATEST_NOTES', response.data)
      commit('UPDATE_TOTAL_PAGES', response.headers['x-wp-totalpages'])
      return true
    } catch (error) {
      return error
    }
  },

  async getSingleNote ({ commit }, id) {
    try {
      const endpoint = 'field_note'
      const query = `/${id}?_embed`
      const response = await axios.get(`${process.env.VUE_APP_API_ROOT}${endpoint}${query}`)
      commit('UPDATE_LATEST_NOTES', [response.data])
      return true
    } catch (error) {
      return error
    }
  },

  async getNextPageOfLatestNotes ({ commit }) {
    // TODO: PWA Caching?
    try {
      const endpoint = 'field_note'
      const query = `?_embed&per_page=${globals.pagination.perPage}&offset=${this.state.notes.length}`
      const url = `${process.env.VUE_APP_API_ROOT}${endpoint}${query}`
      const response = await axios.get(url)
      commit('APPEND_LATEST_NOTES', response.data)
      commit('INCREMENT_CURRENT_PAGE', response.data)
      return true
    } catch (error) {
      return error
    }
  },

  async getNextPageOfLatestNotesForUser ({ commit }, payload) {
    // TODO: PWA Caching?
    try {
      const endpoint = 'field_note'
      const query = `?_embed&author=${payload}&per_page=${globals.pagination.perPage}&offset=${this.state.notes.length}`
      const url = `${process.env.VUE_APP_API_ROOT}${endpoint}${query}`
      const response = await axios.get(url)
      commit('APPEND_LATEST_NOTES', response.data)
      commit('INCREMENT_CURRENT_PAGE', response.data)
      return true
    } catch (error) {
      return error
    }
  },

  async getNearbyNotes ({ commit }, { latitude, longitude, radius }) {
    // TODO: PWA Caching?
    commit('SET_LOADING', true)
    try {
      const endpoint = 'nearby'
      const query = `?lat=${latitude}&lon=${longitude}&rad=${radius}&per_page=${globals.pagination.perPage}`
      const url = `${process.env.VUE_APP_API_AC_ROOT}${endpoint}${query}`
      let response = await axios.get(url)

      commit('UPDATE_NEARBY_NOTES', response.data)
      commit('SET_LOADING', false)
      return true
    } catch (error) {
      commit('SET_LOADING', false)
      return error
    }
  },

  async getNextPageOfNearbyNotes ({ commit }, { latitude, longitude, radius }) {
    // TODO: PWA Caching?
    try {
      const endpoint = 'nearby'
      const query = `?lat=${latitude}&lon=${longitude}&rad=${radius}&offset=${this.state.nearbynotes.length}&per_page=${globals.pagination.perPage}`
      const url = `${process.env.VUE_APP_API_AC_ROOT}${endpoint}${query}`
      const response = await axios.get(url)
      commit('APPEND_NEARBY_NOTES', response.data)
      return true
    } catch (error) {
      throw error
    }
  },

  async getUserNotes ({ state, getters, commit }) {
    // TODO: PWA Caching?
    if (!getters.loggedIn) return
    commit('SET_LOADING', true)
    try {
      const endpoint = 'field_note'
      const query = `?_embed&author=${state.userInfo.id}&per_page=${globals.pagination.perPage}`
      let response = await axios.get(`${process.env.VUE_APP_API_ROOT}${endpoint}${query}`)
      commit('UPDATE_MY_NOTES', response.data)
      commit('SET_LOADING', false)
      return true
    } catch (error) {
      commit('SET_LOADING', false)
      return error
    }
  },

  async getNextPageOfUserNotes ({ state, getters, commit }) {
    // TODO: PWA Caching?
    if (!getters.loggedIn) return
    try {
      const endpoint = 'field_note'
      const query = `?_embed&author=${state.userInfo.id}&per_page=${globals.pagination.perPage}&offset=${this.state.mynotes.length}`
      const url = `${process.env.VUE_APP_API_ROOT}${endpoint}${query}`
      const response = await axios.get(url)
      commit('APPEND_MY_NOTES', response.data)
      return true
    } catch (error) {
      return error
    }
  },

  async deleteNote ({ state, getters, commit }, id) {
    if (!getters.loggedIn) return
    try {
      const url = process.env.VUE_APP_API_ROOT + `field_note/${id}?force=true`
      const res = await axios(url, {
        method: 'delete',
        headers: {
          'Authorization': `Basic ${state.appCredentials}`
        }
      })

      if (res.status > 204) {
        throw new Error(res.status)
      }

      commit('DELETE_NOTE', id)

    } catch (err) {
      throw err
    }
  },

  //

  getTags (_, tagType) {
    const params = {
      orderby: 'name',
      per_page: 100
    }
    return axios.get(process.env.VUE_APP_API_ROOT + tagType, {
      params
    })
  },

  updateLocation ({ commit }) {
    getLocationFromDevice()
      .then(location => {
        commit('UPDATE_LOCATION', location)
      })
      .catch(err => {
        console.error(err)
      })
  },

  startLocationPoll ({ dispatch }) {
    dispatch('updateLocation')
    setInterval(() => {
      dispatch('updateLocation')
    }, 15 * 1000)
  },

  addToHomescreen ({ commit }) {
    const md = new MobileDetect(window.navigator.userAgent)

    if (md.mobile()) {
      const browser = md.userAgent()
      if (browser === 'Safari' || browser === 'Chrome') {
        commit('SET_SHOW_ADD_TO_HOMESCREEN', true)
      }
    }

    commit('FLAG_PROMPTED_TO_ADD_PWA', true)
  },

  setBanner ({ state, commit }, { message, duration }) {
    commit('SET_BANNER', {
      show: true,
      text: message
    })

    if (duration === undefined) return

    setTimeout(() => {
      if (state.bannerStatus.text === message) {
        commit('SET_BANNER', {
          show: false
        })
      }
    }, duration)
  },

  closeBanner ({ commit }) {
    commit('SET_BANNER', {
      show: false,
    })
  }
}
